(function () {
  "use strict";

  const settings = document.querySelector("script[data-gwp]")?.dataset || {};
  const gwpEnabled = settings.gwpEnabled === "true";
  const giftVariantId = Number(settings.giftVariantId);
  const minimumSubtotalCents = Number(settings.thresholdCents);
  const qualifyingProductIds = (settings.qualifyingProductIds || "")
    .split(",")
    .filter(Boolean)
    .map(Number);
  const freeLabel = "FREE";

  if (!giftVariantId) {
    return;
  }

  const giftCartLineSelector =
    '[data-input-item][data-id="' + giftVariantId + '"]';
  const giftProductOptionSelector =
    'select[name="id"] option[value="' + giftVariantId + '"]';
  const dismissalStorageKey = "gwp-dismissed:" + giftVariantId;

  function isGiftDismissed() {
    try {
      return window.sessionStorage.getItem(dismissalStorageKey) === "1";
    } catch (error) {
      return false;
    }
  }

  function markGiftDismissed() {
    try {
      window.sessionStorage.setItem(dismissalStorageKey, "1");
    } catch (error) {}
  }

  function readJson(response) {
    return response.json();
  }

  function getCart() {
    return fetch("/cart.js").then(readJson);
  }

  function addGiftToCart() {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [
          { id: giftVariantId, quantity: 1 },
        ],
      }),
    });
  }

  function updateGiftLineQuantity(lineKey, quantity) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: lineKey, quantity: quantity }),
    });
  }

  function isGiftLine(item) {
    return Number(item.id) === giftVariantId;
  }

  function findGiftLine(cart) {
    return cart.items.find(isGiftLine);
  }

  function isQualifyingItem(item) {
    return qualifyingProductIds.indexOf(item.product_id) !== -1;
  }

  function calculateQualifyingSubtotal(cart) {
    return cart.items.reduce(function (subtotal, item) {
      return isQualifyingItem(item) ? subtotal + item.final_line_price : subtotal;
    }, 0);
  }

  function cartQualifiesForGift(cart) {
    const subtotal = qualifyingProductIds.length
      ? calculateQualifyingSubtotal(cart)
      : cart.items_subtotal_price;
    return subtotal >= minimumSubtotalCents;
  }

  function reportCartChanged() {
    return true;
  }

  function applyGiftRules(cart) {
    const giftLine = findGiftLine(cart);
    const qualifies = cartQualifiesForGift(cart);

    if (qualifies && !giftLine) {
      if (isGiftDismissed()) return false;
      return addGiftToCart().then(reportCartChanged);
    }
    if (!qualifies && giftLine) {
      return updateGiftLineQuantity(giftLine.key, 0).then(reportCartChanged);
    }
    if (qualifies && giftLine && giftLine.quantity !== 1) {
      return updateGiftLineQuantity(giftLine.key, 1).then(reportCartChanged);
    }
    return false;
  }

  function reconcileGiftWithCart() {
    return getCart().then(applyGiftRules);
  }

  function ignoreError() {}

  let pendingReconciliation = Promise.resolve();
  function queueGiftReconciliation() {
    const reconciliation = pendingReconciliation.then(reconcileGiftWithCart);
    pendingReconciliation = reconciliation.catch(ignoreError);
    return reconciliation;
  }

  function getThemeSectionInstances() {
    return window.Shopify?.theme?.sections?.instances || [];
  }

  function refreshSectionIfCart(section) {
    if (section.type === "quick-cart" && section.refreshQuickCart) {
      section.refreshQuickCart();
    }
    if (section.type === "cart" && section.refreshCart) {
      section.refreshCart();
    }
  }

  function refreshCartSections() {
    getThemeSectionInstances().forEach(refreshSectionIfCart);
  }

  function reconcileAndRefresh() {
    queueGiftReconciliation().then(function (cartChanged) {
      if (cartChanged) refreshCartSections();
    });
  }

  function watchCartUpdates() {
    ["cart:updated", "rebuy:cart.add"].forEach(function (eventName) {
      document.addEventListener(eventName, reconcileAndRefresh);
    });
  }

  function bindGiftRemovalButton(giftLineElement) {
    const removeButton = giftLineElement.querySelector("[data-remove-item]");
    if (!removeButton || removeButton.dataset.gwpDismissBound) return;
    removeButton.dataset.gwpDismissBound = "true";
    removeButton.addEventListener("click", markGiftDismissed);
  }

  function decorateGiftLines() {
    const salePriceSelector =
      ".quick-cart__item-price.sale-price, .cart__form-item-price.sale-price";

    document
      .querySelectorAll(giftCartLineSelector)
      .forEach(function (giftLineElement) {
        giftLineElement.classList.add("gwp-gift-line");
        bindGiftRemovalButton(giftLineElement);
        const salePriceElement = giftLineElement.querySelector(salePriceSelector);
        if (
          !salePriceElement ||
          salePriceElement.classList.contains("gwp-free-price")
        ) {
          return;
        }
        salePriceElement.classList.add("gwp-free-price");
        salePriceElement.textContent = freeLabel;
      });
  }

  let decorationScheduled = false;
  function runScheduledDecoration() {
    decorationScheduled = false;
    decorateGiftLines();
  }

  function scheduleGiftLineDecoration() {
    if (decorationScheduled) return;
    decorationScheduled = true;
    window.requestAnimationFrame(runScheduledDecoration);
  }

  const cartSectionSelector =
    '[data-section-type="quick-cart"], [data-section-type="cart"]';

  function watchCartForChanges() {
    const observer = new MutationObserver(scheduleGiftLineDecoration);
    document.querySelectorAll(cartSectionSelector).forEach(function (cartSection) {
      observer.observe(cartSection, { childList: true, subtree: true });
    });
  }

  function isProductPage() {
    return document.body.classList.contains("template-product");
  }

  function markGiftProductPage() {
    if (!isProductPage()) return;
    if (document.querySelector(giftProductOptionSelector)) {
      document.body.classList.add("gwp-gift-product");
    }
  }

  function startGiftPromotion() {
    watchCartUpdates();
    watchCartForChanges();
    decorateGiftLines();
    markGiftProductPage();
    reconcileAndRefresh();
  }

  function removeLeftoverGift() {
    getCart().then(function (cart) {
      const giftLine = findGiftLine(cart);
      if (!giftLine) return;
      updateGiftLineQuantity(giftLine.key, 0).then(refreshCartSections);
    });
  }

  function runWhenReady(callback) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", callback);
    } else {
      callback();
    }
  }

  runWhenReady(gwpEnabled ? startGiftPromotion : removeLeftoverGift);
})();
